<template>
  <div class="veo31-seo-content">
    <section class="veo-block veo-model-explorer">
      <h2>{{ $t('seo.videoModels.veo31.exploreTitle') }}</h2>
      <p>{{ $t('seo.videoModels.veo31.exploreIntro') }}</p>
      <div class="veo-model-links">
        <router-link v-for="item in exploreModels" :key="item.modelId" :to="modelLink(item.modelId)">
          <div><strong>{{ item.name }}</strong><span>{{ item.desc }}</span></div>
          <i aria-hidden="true">→</i>
        </router-link>
      </div>
    </section>

    <section class="veo-block veo-key-features">
      <h2>{{ $t('seo.videoModels.veo31.featuresTitle') }}</h2>
      <p>{{ $t('seo.videoModels.veo31.featuresIntro') }}</p>
      <ul>
        <li v-for="(feature, index) in featureLinks" :key="feature.title">
          <button type="button" @click="scrollToSection(index)">{{ feature.title }}</button>
          <span>{{ feature.desc }}</span>
        </li>
      </ul>
    </section>

    <section
      class="veo-block veo-demo-section"
      :class="{ 'veo-demo-section--reverse': index % 2 === 1 }"
      :id="'veo-feature-' + index"
      v-for="(section, index) in demoSections"
      :key="section.title"
    >
      <div class="veo-demo-copy">
        <span>0{{ index + 1 }} · {{ section.kicker }}</span>
        <h2>{{ section.title }}</h2>
        <p>{{ section.desc }}</p>
        <div class="veo-prompt-box">
          <small>{{ $t('seo.videoModels.demoPrompt') }}</small>
          <p>{{ section.prompt }}</p>
          <button type="button" @click="$emit('use-prompt', section.prompt)">{{ $t('seo.videoModels.usePrompt') }} →</button>
        </div>
      </div>
      <figure class="veo-video-frame">
        <video :src="videoSource(section.media)" controls muted loop playsinline preload="metadata"></video>
        <figcaption>{{ section.caption }}</figcaption>
      </figure>
    </section>

    <section class="veo-block veo-control-section">
      <div class="veo-control-heading">
        <span>CONTROL</span>
        <h2>{{ $t('seo.videoModels.veo31.controlTitle') }}</h2>
        <p>{{ $t('seo.videoModels.veo31.controlIntro') }}</p>
      </div>
      <div class="veo-control-grid">
        <article v-for="item in controls" :key="item.title">
          <span>{{ item.index }}</span>
          <h3>{{ item.title }}</h3>
          <p>{{ item.desc }}</p>
        </article>
      </div>
    </section>

    <section class="veo-block veo-use-cases">
      <div class="veo-use-case-heading">
        <span>USE CASES</span>
        <h2>{{ $t('seo.videoModels.veo31.useCasesTitle') }}</h2>
        <p>{{ $t('seo.videoModels.veo31.useCasesIntro') }}</p>
      </div>
      <div class="veo-use-case-grid">
        <article v-for="(item, index) in useCases" :key="item.title">
          <strong>0{{ index + 1 }}</strong>
          <h3>{{ item.title }}</h3>
          <p>{{ item.desc }}</p>
        </article>
      </div>
    </section>

    <section class="veo-block veo-comparison-section">
      <h2>{{ $t('seo.videoModels.veo31.comparisonTitle') }}</h2>
      <p>{{ $t('seo.videoModels.veo31.comparisonIntro') }}</p>
      <div class="veo-comparison-table">
        <div class="veo-comparison-row veo-comparison-head">
          <span>{{ $t('seo.videoModels.modelColumn') }}</span>
          <span>{{ $t('seo.videoModels.strengthColumn') }}</span>
          <span>{{ $t('seo.videoModels.bestForColumn') }}</span>
        </div>
        <div class="veo-comparison-row" v-for="item in comparisons" :key="item.model">
          <strong>{{ item.model }}</strong>
          <span>{{ item.strength }}</span>
          <span>{{ item.bestFor }}</span>
        </div>
      </div>
    </section>

    <section class="veo-block veo-balance-section">
      <div class="veo-balance-card veo-balance-card--pros">
        <span>{{ $t('seo.videoModels.veo31.prosLabel') }}</span>
        <h2>{{ $t('seo.videoModels.veo31.prosTitle') }}</h2>
        <ul><li v-for="item in pros" :key="item">{{ item }}</li></ul>
      </div>
      <div class="veo-balance-card">
        <span>{{ $t('seo.videoModels.veo31.limitsLabel') }}</span>
        <h2>{{ $t('seo.videoModels.veo31.limitsTitle') }}</h2>
        <ul><li v-for="item in limits" :key="item">{{ item }}</li></ul>
      </div>
    </section>

    <section class="veo-block veo-how-to-section">
      <div class="veo-generator-visual">
        <div class="veo-generator-visual-top"><span>Veo 3.1</span><strong>30 credits</strong></div>
        <p>{{ $t('seo.videoModels.veo31.samplePrompt') }}</p>
        <div class="veo-generator-visual-tags"><span>16:9</span><span>8s</span><span>1080p</span></div>
        <div class="veo-generator-visual-bottom"><span>0/2000</span><button type="button" @click="$emit('back-to-generator')">{{ $t('home.generate') }}</button></div>
      </div>
      <div class="veo-how-to-copy">
        <h2>{{ $t('seo.videoModels.veo31.howToTitle') }}</h2>
        <p>{{ $t('seo.videoModels.veo31.howToIntro') }}</p>
        <article v-for="(step, index) in howToSteps" :key="step.title">
          <strong>0{{ index + 1 }}</strong>
          <div><h3>{{ step.title }}</h3><p>{{ step.desc }}</p></div>
        </article>
        <button type="button" @click="$emit('back-to-generator')">{{ $t('seo.videoModels.veo31.howToCta') }}</button>
      </div>
    </section>

    <section class="veo-block veo-faq-section">
      <h2>{{ $t('seo.videoModels.veo31.faqTitle') }}</h2>
      <p>{{ $t('seo.videoModels.veo31.faqIntro') }}</p>
      <div class="veo-faq-list">
        <details v-for="faq in faqs" :key="faq.q">
          <summary><span>{{ faq.q }}</span><i></i></summary>
          <p>{{ faq.a }}</p>
        </details>
      </div>
    </section>

    <section class="veo-block veo-bottom-cta">
      <div class="veo-cta-media"><video :src="videoSource('veo')" autoplay muted loop playsinline preload="metadata"></video></div>
      <div>
        <span>MIRAL AI · VEO 3.1</span>
        <h2>{{ $t('seo.videoModels.veo31.ctaTitle') }}</h2>
        <p>{{ $t('seo.videoModels.veo31.ctaText') }}</p>
        <button type="button" @click="$emit('back-to-generator')">{{ $t('seo.videoModels.veo31.ctaButton') }}</button>
      </div>
    </section>
  </div>
</template>

<script>
var veoDemo = require('@/assets/images/veo31/68f52096-d419-499e-afdb-19b966c03d2e.mp4')
var textToVideoDemo = require('@/assets/images/veo31/6fec0390-7ddc-4d2d-adda-330887e00312.mp4')
var imageToVideoDemo = require('@/assets/images/veo31/1784276079991.mp4')
var cameraDemo = require('@/assets/images/veo31/1784278783562.mp4')
var viralVideoDemo = require('@/assets/images/veo31/1784278969391.mp4')

module.exports = {
  name: 'Veo31SeoContent',
  props: {
    exploreModels: { type: Array, default: function () { return [] } },
    featureLinks: { type: Array, default: function () { return [] } },
    demoSections: { type: Array, default: function () { return [] } },
    useCases: { type: Array, default: function () { return [] } },
    comparisons: { type: Array, default: function () { return [] } },
    pros: { type: Array, default: function () { return [] } },
    limits: { type: Array, default: function () { return [] } },
    howToSteps: { type: Array, default: function () { return [] } },
    faqs: { type: Array, default: function () { return [] } }
  },
  computed: {
    controls: function () {
      var key = 'seo.videoModels.veo31.controls'
      var value = this.$te(key) ? this.$t(key) : []
      return Array.isArray(value) ? value : []
    }
  },
  methods: {
    // 为探索模型链接保留当前语言前缀。
    modelLink: function (modelId) {
      var lang = this.$route.params.lang
      return (lang ? '/' + lang : '') + '/models/' + modelId
    },
    // 根据内容配置选择站内已压缩的视频演示素材。
    videoSource: function (media) {
      var sources = {
        veo: veoDemo,
        text: textToVideoDemo,
        image: imageToVideoDemo,
        viral: viralVideoDemo,
        camera: cameraDemo
      }
      return sources[media] || veoDemo
    },
    // 滚动到对应的 Veo 3.1 功能说明区。
    scrollToSection: function (index) {
      var target = document.getElementById('veo-feature-' + index)
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
.veo31-seo-content { width: 100%; max-width: 1060px; margin: 0 auto; color: #18191d; }
.veo-block { margin-bottom: 104px; scroll-margin-top: 88px; }
.veo-block > h2, .veo-control-heading h2, .veo-use-case-heading h2 { margin: 0 0 18px; color: #17181c; font-size: clamp(29px, 3.4vw, 40px); line-height: 1.2; letter-spacing: -.035em; text-align: center; }
.veo-block > p, .veo-control-heading p, .veo-use-case-heading p { max-width: 860px; margin: 0 auto 30px; color: #656b75; font-size: 15px; line-height: 1.78; text-align: center; }
.veo-model-links { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.veo-model-links a { display: flex; min-height: 84px; box-sizing: border-box; align-items: center; justify-content: space-between; gap: 12px; padding: 17px 18px; border: 1px solid #e2e4e8; border-radius: 12px; color: #202228; text-decoration: none; transition: transform .2s, border-color .2s; }
.veo-model-links a:hover { transform: translateY(-2px); border-color: #66c9a7; }
.veo-model-links div { display: flex; min-width: 0; flex-direction: column; gap: 6px; }
.veo-model-links strong { font-size: 14px; }
.veo-model-links span { color: #858b95; font-size: 11px; line-height: 1.4; }
.veo-model-links i { color: #059669; font-size: 19px; font-style: normal; }
.veo-key-features { max-width: 920px; margin-right: auto; margin-left: auto; }
.veo-key-features ul { margin: 0; padding: 0; border-top: 1px solid #e2e4e7; list-style: none; }
.veo-key-features li { display: grid; grid-template-columns: minmax(210px,.68fr) minmax(0,1.32fr); gap: 24px; padding: 20px 4px; border-bottom: 1px solid #e2e4e7; }
.veo-key-features button { padding: 0; border: 0; background: transparent; color: #078562; cursor: pointer; font-size: 15px; font-weight: 750; text-align: left; }
.veo-key-features li span { color: #656b75; font-size: 14px; line-height: 1.65; }
.veo-demo-section { display: grid; grid-template-columns: minmax(0,.88fr) minmax(360px,1.12fr); gap: 58px; align-items: center; }
.veo-demo-section--reverse { grid-template-columns: minmax(360px,1.12fr) minmax(0,.88fr); }
.veo-demo-section--reverse .veo-demo-copy { order: 2; }
.veo-demo-section--reverse .veo-video-frame { order: 1; }
.veo-demo-copy > span, .veo-control-heading > span, .veo-use-case-heading > span { color: #059669; font-size: 11px; font-weight: 800; letter-spacing: .12em; }
.veo-demo-copy h2 { margin: 10px 0 15px; font-size: clamp(28px,3.1vw,38px); line-height: 1.22; letter-spacing: -.032em; }
.veo-demo-copy > p { margin: 0 0 25px; color: #656b75; font-size: 14px; line-height: 1.78; }
.veo-prompt-box { padding: 19px 20px; border: 1px solid #e1e4e8; border-radius: 12px; background: #f8f9fa; }
.veo-prompt-box small { color: #969ca5; font-size: 10px; font-weight: 750; letter-spacing: .08em; text-transform: uppercase; }
.veo-prompt-box p { margin: 10px 0 13px; color: #393e47; font-size: 13px; line-height: 1.65; }
.veo-prompt-box button { padding: 0; border: 0; background: transparent; color: #078562; cursor: pointer; font-size: 12px; font-weight: 750; }
.veo-video-frame { overflow: hidden; margin: 0; border: 1px solid #dfe2e6; border-radius: 16px; background: #eef0f2; box-shadow: 0 18px 44px rgba(25,31,40,.08); }
.veo-video-frame video { display: block; width: 100%; height: 360px; background: #111318; object-fit: cover; }
.veo-video-frame figcaption { min-height: 42px; box-sizing: border-box; padding: 12px 15px; color: #777d87; background: #fff; font-size: 11px; line-height: 1.55; }
.veo-control-section { padding: 72px 58px; border-radius: 26px; background: radial-gradient(circle at 88% 9%, rgba(102,226,189,.22), transparent 30%), linear-gradient(145deg,#f8f9fa,#eef2f4); }
.veo-control-heading, .veo-use-case-heading { max-width: 780px; margin: 0 auto 36px; text-align: center; }
.veo-control-heading h2, .veo-use-case-heading h2 { margin-top: 11px; }
.veo-control-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; }
.veo-control-grid article { min-height: 160px; padding: 23px; border: 1px solid rgba(220,224,228,.9); border-radius: 14px; background: rgba(255,255,255,.82); }
.veo-control-grid article > span { color: #059669; font-size: 11px; font-weight: 800; }
.veo-control-grid h3 { margin: 32px 0 8px; font-size: 16px; }
.veo-control-grid p { margin: 0; color: #6d737d; font-size: 13px; line-height: 1.65; }
.veo-use-cases { padding: 70px 60px; border: 1px solid #e7e9ec; border-radius: 26px; }
.veo-use-case-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; }
.veo-use-case-grid article { padding: 24px; border-radius: 14px; background: #f7f8f9; }
.veo-use-case-grid strong { color: #059669; font-size: 12px; }
.veo-use-case-grid h3 { margin: 26px 0 9px; font-size: 16px; }
.veo-use-case-grid p { margin: 0; color: #717782; font-size: 13px; line-height: 1.65; }
.veo-comparison-table { overflow: hidden; margin-top: 34px; border: 1px solid #dfe2e6; border-radius: 13px; }
.veo-comparison-row { display: grid; grid-template-columns: .72fr 1.2fr 1.08fr; gap: 24px; align-items: center; padding: 21px 20px; border-top: 1px solid #e2e4e7; }
.veo-comparison-row:first-child { border-top: 0; }
.veo-comparison-head { background: #f2f3f5; color: #3a3e46; font-size: 12px; font-weight: 750; }
.veo-comparison-row strong { color: #078562; font-size: 14px; }
.veo-comparison-row > span { color: #616772; font-size: 13px; line-height: 1.6; }
.veo-balance-section { display: grid; grid-template-columns: repeat(2,1fr); gap: 16px; }
.veo-balance-card { padding: 38px; border: 1px solid #e1e4e8; border-radius: 18px; background: #fafafb; }
.veo-balance-card--pros { background: #f1faf6; }
.veo-balance-card > span { color: #078562; font-size: 11px; font-weight: 800; letter-spacing: .1em; }
.veo-balance-card h2 { margin: 10px 0 25px; font-size: 25px; }
.veo-balance-card ul { display: grid; gap: 13px; margin: 0; padding: 0; list-style: none; }
.veo-balance-card li { position: relative; padding-left: 20px; color: #626873; font-size: 13px; line-height: 1.65; }
.veo-balance-card li::before { content: ''; position: absolute; top: 8px; left: 0; width: 7px; height: 7px; border-radius: 50%; background: #13a77b; }
.veo-how-to-section { display: grid; grid-template-columns: .9fr 1.1fr; gap: 62px; align-items: center; }
.veo-generator-visual { display: flex; min-height: 500px; box-sizing: border-box; padding: 32px; border: 1px solid #dfe2e6; border-radius: 16px; background: radial-gradient(circle at 86% 10%,rgba(102,226,189,.35),transparent 30%),linear-gradient(145deg,#f8f9fa,#eef1f5); flex-direction: column; box-shadow: 0 20px 45px rgba(30,34,42,.08); }
.veo-generator-visual-top, .veo-generator-visual-bottom { display: flex; align-items: center; justify-content: space-between; }
.veo-generator-visual-top span { font-size: 15px; font-weight: 800; }
.veo-generator-visual-top strong { padding: 7px 10px; border-radius: 999px; background: #eaf8f2; color: #087b5d; font-size: 11px; }
.veo-generator-visual > p { flex: 1; margin: 50px 0 24px; color: #4f5661; font-size: 15px; line-height: 1.75; }
.veo-generator-visual-tags { display: flex; gap: 8px; margin-bottom: 24px; }
.veo-generator-visual-tags span { padding: 8px 10px; border: 1px solid #d8dce1; border-radius: 8px; background: #fff; color: #626873; font-size: 11px; }
.veo-generator-visual-bottom { padding-top: 20px; border-top: 1px solid #dfe2e6; }
.veo-generator-visual-bottom span { color: #9aa0a9; font-size: 11px; }
.veo-generator-visual-bottom button, .veo-how-to-copy > button, .veo-bottom-cta button { padding: 12px 19px; border: 0; border-radius: 9px; background: #17191e; color: #fff; cursor: pointer; font-size: 12px; font-weight: 750; }
.veo-how-to-copy > h2 { margin: 0 0 14px; font-size: clamp(29px,3.3vw,40px); line-height: 1.2; letter-spacing: -.035em; }
.veo-how-to-copy > p { margin: 0 0 27px; color: #666c76; font-size: 14px; line-height: 1.7; }
.veo-how-to-copy article { display: grid; grid-template-columns: 48px 1fr; gap: 18px; padding: 19px 0; border-top: 1px solid #e2e4e7; }
.veo-how-to-copy article > strong { color: #078562; font-size: 22px; }
.veo-how-to-copy h3 { margin: 0 0 7px; font-size: 15px; }
.veo-how-to-copy article p { margin: 0; color: #686e77; font-size: 13px; line-height: 1.65; }
.veo-how-to-copy > button { margin-top: 21px; }
.veo-faq-section { max-width: 860px; margin-right: auto; margin-left: auto; }
.veo-faq-list { border-top: 1px solid #dfe1e5; }
.veo-faq-list details { border-bottom: 1px solid #dfe1e5; }
.veo-faq-list summary { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 22px 2px; cursor: pointer; font-size: 15px; font-weight: 700; list-style: none; }
.veo-faq-list summary::-webkit-details-marker { display: none; }
.veo-faq-list summary i { position: relative; width: 15px; height: 15px; flex: 0 0 15px; }
.veo-faq-list summary i::before, .veo-faq-list summary i::after { content: ''; position: absolute; top: 7px; left: 1px; width: 13px; height: 1px; background: #6d737c; }
.veo-faq-list summary i::after { transform: rotate(90deg); transition: transform .2s; }
.veo-faq-list details[open] summary i::after { transform: rotate(0); }
.veo-faq-list details p { max-width: 760px; margin: -2px 0 22px; color: #656b75; font-size: 14px; line-height: 1.78; }
.veo-bottom-cta { display: grid; min-height: 360px; overflow: hidden; grid-template-columns: .95fr 1.05fr; border-radius: 18px; background: #15171c; color: #fff; }
.veo-cta-media { min-height: 360px; background: #0d0f12; }
.veo-cta-media video { display: block; width: 100%; height: 100%; min-height: 360px; object-fit: cover; }
.veo-bottom-cta > div:last-child { align-self: center; padding: 48px; }
.veo-bottom-cta > div > span { color: #6ee7b7; font-size: 11px; font-weight: 800; letter-spacing: .1em; }
.veo-bottom-cta h2 { margin: 13px 0 14px; color: #fff; font-size: clamp(29px,3.2vw,40px); line-height: 1.18; }
.veo-bottom-cta p { margin: 0; color: #bfc3ca; font-size: 14px; line-height: 1.72; }
.veo-bottom-cta button { margin-top: 24px; background: #fff; color: #17191e; }
@media (max-width: 900px) {
  .veo-model-links { grid-template-columns: repeat(2,1fr); }
  .veo-demo-section, .veo-demo-section--reverse, .veo-how-to-section { grid-template-columns: 1fr; gap: 34px; }
  .veo-demo-section--reverse .veo-demo-copy, .veo-demo-section--reverse .veo-video-frame { order: initial; }
  .veo-control-grid, .veo-use-case-grid { grid-template-columns: repeat(2,1fr); }
  .veo-generator-visual { min-height: 420px; }
}
@media (max-width: 640px) {
  .veo-block { margin-bottom: 76px; }
  .veo-block > h2, .veo-control-heading h2, .veo-use-case-heading h2 { font-size: 28px; }
  .veo-model-links, .veo-control-grid, .veo-use-case-grid, .veo-balance-section { grid-template-columns: 1fr; }
  .veo-key-features li, .veo-comparison-row { grid-template-columns: 1fr; gap: 8px; }
  .veo-demo-section { gap: 24px; }
  .veo-video-frame video { height: 250px; }
  .veo-control-section, .veo-use-cases { margin-right: -18px; margin-left: -18px; padding: 48px 18px; border-radius: 0; }
  .veo-comparison-head { display: none; }
  .veo-comparison-row { gap: 9px; }
  .veo-generator-visual { min-height: 360px; padding: 24px; }
  .veo-bottom-cta { margin-right: -18px; margin-left: -18px; grid-template-columns: 1fr; border-radius: 0; }
  .veo-cta-media, .veo-cta-media video { min-height: 250px; height: 250px; }
  .veo-bottom-cta > div:last-child { padding: 38px 22px; }
}
</style>
